import { useLang } from "../../context/LanguageContext";
import ChaletMap from "./ChaletMap";
import { MapPin, Flag, Gem, Snowflake, Car } from "lucide-react";

const poiData = [
  {
    image: "https://images.unsplash.com/photo-1783104122400-d5550ad5dafb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    distance: "25 min",
    Icon: Flag,
  },
  {
    image: "https://images.unsplash.com/photo-1626797236912-978b89528ce5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    distance: "5 min",
    Icon: Gem,
  },
  {
    image: "https://images.unsplash.com/photo-1489664729864-ee8499a4ddd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    distance: "20 min",
    Icon: Snowflake,
  },
];

export default function Location() {
  const { t } = useLang();
  return (
    <section id="location" data-testid="location-section" className="section-pad bg-[hsl(var(--secondary))]">
      <div className="container-x">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="overline mb-5">{t.location.eyebrow}</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))] whitespace-pre-line">
              {t.location.title}
            </h2>
            <p className="mt-7 text-base md:text-lg text-[hsl(var(--foreground))]/85 leading-relaxed">
              {t.location.body}
            </p>
            {/* Coordinates badge */}
            <div className="mt-8 inline-flex items-center gap-3 bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/8 px-5 py-3 w-fit" style={{ borderRadius: "6px" }}>
              <MapPin className="w-4 h-4 text-[hsl(var(--accent))]" />
              <span className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--foreground))]/75 font-medium">
                47.3192° N · 14.4133° E · 1.070 m
              </span>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden bg-[hsl(var(--muted))] border border-[hsl(var(--foreground))]/10 shadow-lg shadow-black/5" style={{ borderRadius: "6px" }}>
              <div className="aspect-[16/10]">
                <ChaletMap />
              </div>
            </div>
            {/* Map caption */}
            <div className="mt-4 flex items-center gap-4 text-xs text-[hsl(var(--foreground))]/60">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--accent))]" />
                Chalet Steiermark
              </span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">Pusterwald, Steiermark</span>
            </div>
          </div>
        </div>

        {/* Nearby Section */}
        <div className="mt-20 lg:mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="overline">{t.location.nearby}</div>
            <div className="flex-1 h-px bg-[hsl(var(--foreground))]/10" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {t.location.poi.map((p, i) => {
              const poi = poiData[i];
              const IconComp = poi?.Icon;
              return (
                <div
                  key={i}
                  data-testid={`poi-${i}`}
                  className="group relative bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/6 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-black/8 hover:-translate-y-1"
                  style={{ borderRadius: "6px" }}
                >
                  {/* Image */}
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={poi?.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Distance badge */}
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-[hsl(var(--foreground))] text-[11px] font-semibold tracking-wide uppercase px-3 py-1.5 flex items-center gap-1.5 shadow-sm" style={{ borderRadius: "4px" }}>
                      <Car className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                      {poi?.distance}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" style={{ borderRadius: "4px" }}>
                        {IconComp && <IconComp className="w-4 h-4" />}
                      </div>
                      <h3 className="font-serif text-xl text-[hsl(var(--foreground))]">{p.name}</h3>
                    </div>
                    <p className="mt-2 text-sm text-[hsl(var(--foreground))]/70 leading-relaxed pl-11">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
